import { isValidSourceNumber } from './inlineCitations';

/* The few mdast fields this plugin touches */
interface MarkdownNode {
  type: string;
  value?: string;
  children?: MarkdownNode[];
  data?: Record<string, unknown>;
}

interface RemarkInlineCitationsOptions {
  /* Streaming text has bare `[3]` markers and no sources to check them against yet */
  streaming: boolean;
  sourcesCount: number;
}

const STREAMED_MARKER = /\[(\d+)\]/g;
const LINK_LABEL_MARKER = /^\[(\d+)\]$/;
const TRAILING_WORD = /(\S*)\s*$/;

/*
 * A word longer than this (a long identifier, say) is not kept on the line
 * with its marker: a group that can't wrap would overflow a narrow card.
 * Bare URLs never get here; markdown turns them into links first.
 */
const MAX_GLUED_WORD_LENGTH = 24;
const LEADING_PUNCTUATION = /^[.,;:!?)\]]+/;

/* Rendered by the `cite` entry in the Markdown components map */
const createMarker = (source: number, pending: boolean): MarkdownNode => ({
  type: 'inlineCitation',
  data: {
    hName: 'cite',
    hProperties: { dataSource: source, dataPending: pending },
    hChildren: [{ type: 'text', value: `[${source}]` }],
  },
});

const createGroup = (children: MarkdownNode[]): MarkdownNode => ({
  type: 'inlineCitationGroup',
  children,
  data: { hName: 'span', hProperties: { style: 'white-space: nowrap' } },
});

/*
 * Wraps each marker with the word before it and the punctuation after it,
 * so a line never starts with an indicator or with the "." that follows
 * one. The space before the marker is dropped: the indicator's own margin
 * sets the gap, as it does for legacy citations.
 */
const glueMarkers = (children: MarkdownNode[]): MarkdownNode[] => {
  const glued: MarkdownNode[] = [];

  children.forEach((node, i) => {
    if (node.type !== 'inlineCitation') {
      glued.push(node);
      return;
    }

    const group: MarkdownNode[] = [node];
    const previous = glued[glued.length - 1];
    if (previous?.type === 'text' && previous.value) {
      const [trailing, word] = previous.value.match(TRAILING_WORD) ?? ['', ''];
      const glueWord = word.length > 0 && word.length <= MAX_GLUED_WORD_LENGTH;
      // Always drop the space before the marker; take the word along only if short
      previous.value = previous.value.slice(
        0,
        previous.value.length -
          (glueWord ? trailing.length : trailing.length - word.length)
      );
      if (glueWord) group.unshift({ type: 'text', value: word });
    }

    const next = children[i + 1];
    const punctuation =
      next?.type === 'text' ? next.value?.match(LEADING_PUNCTUATION)?.[0] : '';
    if (next && punctuation) {
      group.push({ type: 'text', value: punctuation });
      next.value = next.value?.slice(punctuation.length);
    }

    glued.push(createGroup(group));
  });

  return glued.filter((node) => !(node.type === 'text' && !node.value));
};

const setStatement = (marker: MarkdownNode, statement: string) => {
  const properties = (marker.data?.hProperties ?? {}) as Record<
    string,
    unknown
  >;
  properties.dataStatement = statement;
};

const plainText = (node: MarkdownNode): string => {
  if (node.type === 'inlineCitation') return '';
  if (node.type === 'text' || node.type === 'inlineCode') {
    return node.value ?? '';
  }
  return (node.children ?? []).map(plainText).join('');
};

/* Text with its markers removed, without the separators they leave behind */
const toStatement = (node: MarkdownNode): string =>
  plainText(node)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/,+(?=[.;:]|$)/g, '')
    .trim();

const splitStreamedMarkers = (
  text: MarkdownNode,
  markers: MarkdownNode[]
): MarkdownNode[] => {
  const value = text.value ?? '';
  const nodes: MarkdownNode[] = [];
  let last = 0;

  for (const match of value.matchAll(STREAMED_MARKER)) {
    const index = match.index ?? 0;
    if (index > last) {
      nodes.push({ type: 'text', value: value.slice(last, index) });
    }
    const marker = createMarker(Number(match[1]), true);
    markers.push(marker);
    nodes.push(marker);
    last = index + match[0].length;
  }

  if (nodes.length === 0) return [text];
  if (last < value.length) {
    nodes.push({ type: 'text', value: value.slice(last) });
  }
  return nodes;
};

const transformChildren = (
  node: MarkdownNode,
  options: RemarkInlineCitationsOptions,
  markers: MarkdownNode[]
) => {
  if (!node.children) return;

  const children: MarkdownNode[] = [];
  for (const child of node.children) {
    if (child.type === 'link') {
      // `[[3]](url)` parses as a link whose only text is `[3]`
      const label =
        child.children?.length === 1 && child.children[0].type === 'text'
          ? child.children[0].value
          : undefined;
      const match = label?.match(LINK_LABEL_MARKER);
      const source = match ? Number(match[1]) : NaN;

      if (
        match &&
        (options.streaming || isValidSourceNumber(source, options.sourcesCount))
      ) {
        const marker = createMarker(source, options.streaming);
        markers.push(marker);
        children.push(marker);
      } else {
        // Out-of-range markers and ordinary links stay links
        children.push(child);
      }
    } else if (options.streaming && child.type === 'text') {
      children.push(...splitStreamedMarkers(child, markers));
    } else {
      visit(child, options, markers);
      children.push(child);
    }
  }
  node.children = glueMarkers(children);
};

const visit = (
  node: MarkdownNode,
  options: RemarkInlineCitationsOptions,
  markers: MarkdownNode[]
) => {
  if (node.type === 'tableRow' && node.children) {
    // A cell alone ("September 2026 [5]") means little; prefix the row's label
    const cells = node.children;
    const cellMarkers = cells.map((cell) => {
      const own: MarkdownNode[] = [];
      transformChildren(cell, options, own);
      markers.push(...own);
      return own;
    });
    const label = toStatement(cells[0]);
    cells.forEach((cell, i) => {
      const text = toStatement(cell);
      const statement = i === 0 || !label ? text : `${label} — ${text}`;
      cellMarkers[i].forEach((marker) => setStatement(marker, statement));
    });
    return;
  }

  if (node.type === 'paragraph' || node.type === 'heading') {
    const own: MarkdownNode[] = [];
    transformChildren(node, options, own);
    const statement = toStatement(node);
    own.forEach((marker) => setStatement(marker, statement));
    markers.push(...own);
    return;
  }

  transformChildren(node, options, markers);
};

/*
 * Turns inline citation markers into `cite` elements carrying the source
 * number, whether it is still streaming, and the statement it supports.
 */
const remarkInlineCitations =
  (options: RemarkInlineCitationsOptions) => (tree: MarkdownNode) => {
    visit(tree, options, []);
  };

export default remarkInlineCitations;
